import {getAzureFontFromGoogleFont} from "@esWebAPI/es/model/Fonts";
import {ImageSpriteGenerator} from "@esWebAPI/es/map/layer/ImageSpriteGenerator";

import type {Expression, IconOptions, TextOptions} from "azure-maps-control"
import type {DataDrivenPropertyValueSpecification} from "@maplibre/maplibre-gl-style-spec";
import type {BasicText, LabelSettings, StyleSettings} from "@esWebAPI/es/model/Models";
import {ColorThematic, SizeThematic} from "@esWebAPI/es/model/Thematic";
import type {Attribute} from "@esWebAPI/es/model/Feature";
import type {PositionAnchor} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import type {ILayerView} from "@esWebAPI/es/model/Layer";
import type {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import type {SymbolThematicStyler} from "@esWebAPI/es/map/styler/SymbolThematicStyler";
import {ColorThematicStyler, DefaultColorThematicStyler} from "@esWebAPI/es/map/styler/ColorThematicStyler";
import {IMapLayer} from "@esWebAPI/es/map/layer/MapLayer";

export class LabelThematicStyler {
    private readonly basicText: BasicText;
    private readonly layerView: ILayerView;
    private readonly labelSettings: LabelSettings
    private readonly styleSettings: StyleSettings;
    private readonly sizeThematic: SizeThematic | undefined;
    private readonly colorThematic: ColorThematic | undefined;
    private readonly attributes: Attribute[];
    private readonly _spriteGenerator: ImageSpriteGenerator|undefined;

    private SYMBOL_MIN_SIZE = 15;
    private SYMBOL_MAX_SIZE = 150;
    private LABEL_MAX_OFFSET = -8.5;

    constructor(layerView: ILayerView, attributes: Attribute[], spriteManager?: AzureImageSpriteManager, sizeThematic?: SizeThematic, colorThematic?: ColorThematic) {

        if (layerView.styleSettings.labelSettings.enabled && !layerView.styleSettings.defaultStyle?.text) {
            es.log.log("LabelThematicStyler called with no defaultStyle.text prop. using default", layerView.styleSettings);
            this.basicText = es.api.style.getDefaultBasicText();
        } else {
            this.basicText = layerView.styleSettings.defaultStyle.text;
        }

        this.layerView =layerView;
        this.attributes = attributes;
        this.labelSettings = layerView.styleSettings.labelSettings;
        this.styleSettings = layerView.styleSettings;
        this.sizeThematic = sizeThematic;
        this.colorThematic = colorThematic;
        if (spriteManager) {
            this._spriteGenerator = new ImageSpriteGenerator(spriteManager);
        }
    }

    /**
     * Create TextOptions to apply to a Label layer (SymbolLayer).
     */
    public getLabelOptions(symbolThematicStyler?: SymbolThematicStyler,colorThematicStyler?: ColorThematicStyler): TextOptions {

        if (!this.labelSettings.enabled) {
            return {};
        }

        const fontFamily = getAzureFontFromGoogleFont(this.basicText.family, this.basicText.bold, this.basicText.italic);

        const textOptions = {
            font: [fontFamily],
            size: this.basicText.height + 2,
            allowOverlap: false,
            ignorePlacement: false,
            optional: true,
            justify: "center",
            textField: this.getTextExpression(this.labelSettings),
            offset: this.getOffsetExpression(this.sizeThematic, symbolThematicStyler),
            anchor: "top",
            opacity: this.getOpacity()
        } as TextOptions;

        if (this.labelSettings.labelling === "OPTIMIZED") {
            const variableAnchor = ["left", "right", "top-left", "bottom-left", "top-right", "bottom-right"] as NonNullable<TextOptions["variableAnchor"]>;
            // The anchor variable is relative to the label and not the point.
            // This means that if we want the label to show BELOW the point we need to set the anchor to *top*
            if (this.styleSettings.$type === "PolygonStyleSettings") {
                variableAnchor.unshift("center", "top", "bottom");
            } else {
                switch (this.basicText?.position) {
                    case "CENTRAL":
                        variableAnchor.unshift("center", "top", "bottom");
                        break;
                    case "ABOVE":
                        variableAnchor.unshift("bottom", "top", "center");
                        break;
                    case "BELOW":
                        variableAnchor.unshift("top", "bottom", "center");
                }
            }

            textOptions.radialOffset = this.styleSettings.defaultStyle.size ?
                (this.styleSettings.defaultStyle.size / this.basicText.height) //number. Units in ems
                : 1;

            textOptions.variableAnchor = variableAnchor;
            //es.log.log(this.labelSettings.labelling, textOptions.variableAnchor);
        } else {
            let anchor: PositionAnchor;
            // Always Set the label at the center of the polygon for polygons layer
            if (this.styleSettings.$type === "PolygonStyleSettings") {
                anchor = "center";
            } else {
                switch (this.basicText?.position) {
                    case "ABOVE":
                        anchor = "bottom";
                        break;
                    case "CENTRAL":
                        anchor = "center";
                        break;
                    //case "BELOW":
                    default:
                        anchor = "top";
                }
            }
            textOptions.anchor = anchor;
            textOptions.variableAnchor = undefined;
            textOptions.radialOffset = undefined;


            if(this.labelSettings.labelling === "ALL") {
                textOptions.allowOverlap = true;
                textOptions.ignorePlacement = true;
                textOptions.optional = false;
            }
            //es.log.log(this.labelSettings.labelling, textOptions.anchor);
        }

        if(this.basicText?.fontOutlineColor){
            textOptions.haloColor = es.util.color.getHex(this.basicText.fontOutlineColor);
            textOptions.haloWidth = 1;
            textOptions.haloBlur = 1;
        } else {
            textOptions.haloColor = "rgba(0,0,0,0)";
            textOptions.haloWidth = 0;
            textOptions.haloBlur = 0;
        }

        if (this.basicText.inheritFontColor && colorThematicStyler) {
            textOptions.color = colorThematicStyler.getHexExpression(this.layerView, this.colorThematic);
        } else {
            textOptions.color = es.util.color.getHex(this.basicText.color);
        }

        return textOptions;
    }

    /**
     * Create IconOptions to apply to a Label layer (SymbolLayer). The icon is used to show the boxes.
     */
    public getIconOptions(): IconOptions {

        const iconOptions: IconOptions = {
            image: ["case", ["==", ["get", "cluster"], true], "none", "box"]
        };

        if (!this.labelSettings.enabled) {
            return iconOptions;
        }

        if (this.isLabelBoxEnabled()) {
            if (!this._spriteGenerator) {
                iconOptions.image = "none";
                es.log.warn("Label box is enabled but styler has no spriteGenerator.");
            } else {
                const imageId = this._spriteGenerator.generateBoxImage();
                iconOptions.image = ["case", ["==", ["get", "cluster"], true], "none", imageId];
            }
        } else {
            iconOptions.image = "none";
        }
        iconOptions.optional = !this.isLabelBoxEnabled();
        iconOptions.anchor = "center";
        iconOptions.opacity = this.getOpacity();
        iconOptions.allowOverlap = !this.isLabelBoxEnabled() && this.labelSettings.labelling === "ALL";

        return iconOptions;
    }

    private isLabelBoxEnabled() {
        return !!this.styleSettings.defaultStyle.text && this.styleSettings.defaultStyle.text.boxFillColor != undefined;
    }

    private getOffsetExpression(sizeThematic?: SizeThematic, _symbolThematicStyler?: SymbolThematicStyler): DataDrivenPropertyValueSpecification<[number, number]> {

        if (this.styleSettings.defaultStyle.$type === "PolygonStyle") {
            return [0, 0];
        }

        if (!sizeThematic || !this.styleSettings.sizeThematicSettings?.enabled) {

            const size = this.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle" ?
                this.styleSettings.defaultStyle.symbolStyle.size :
                this.styleSettings.defaultStyle.size;

            if (this.basicText.position === "CENTRAL") {
                return [0, 0];
            }

            if (this.basicText.position === "BELOW") {
                const offset: DataDrivenPropertyValueSpecification<[number, number]> =
                    [0, Math.max(1, -size * (this.LABEL_MAX_OFFSET)/this.SYMBOL_MAX_SIZE)];
                if (this.isLabelBoxEnabled()) {
                    // Add a bit of offset to compensate for the box padding
                    offset[1] += 0.3;
                }
                return offset;
            }

            // position === "ABOVE"
            // Calculate the offset based on the size of the symbol
            const offset: DataDrivenPropertyValueSpecification<[number, number]>  =
                [0, Math.min(-1.2, size * this.LABEL_MAX_OFFSET/this.SYMBOL_MAX_SIZE)];
            if (this.isLabelBoxEnabled()) {
                // Add a bit of offset to compensate for the box padding
                offset[1] -= 0.3;
            }
            return offset;
        }

        const minOffset = sizeThematic.minSize * -1 / this.SYMBOL_MIN_SIZE;
        const maxOffset = sizeThematic.maxSize * this.LABEL_MAX_OFFSET / this.SYMBOL_MAX_SIZE;
        //const midValue = sizeThematic.maxValue - sizeThematic.minValue;
        const midValue = (sizeThematic.maxValue + sizeThematic.minValue) / 2;
        const midOffset = maxOffset - minOffset  + 1; //FIXME :: 1 is a magic number to prevent overlap at a certain symbol size

        const rs = ["interpolate", ["linear"],
            ["get", sizeThematic.attribute],
            sizeThematic.minValue, ["literal", [0, minOffset]],
            midValue, ["literal", [0, midOffset]],
            sizeThematic.maxValue, ["literal", [0, maxOffset]]
        ] as DataDrivenPropertyValueSpecification<[number, number]>;

        es.log.log("getOffsetExpression", rs);
        return rs;
    }

    public getBoxFillColor(colorThematicStyler?: ColorThematicStyler) {
        if (this.basicText?.inheritBoxFill) {
            colorThematicStyler = colorThematicStyler ? colorThematicStyler : new DefaultColorThematicStyler();
            return colorThematicStyler.getHexExpression(this.layerView, this.colorThematic);
        } else {
            return es.util.color.serverToHex(this.basicText?.boxFillColor || es.util.color.WHITE);
        }
    }

    public getBoxOutlineColor() {
        return es.util.color.serverToHex(this.basicText?.boxOutlineColor || es.util.color.NEW_FONT_COLOR_NAVY);
    }

    /**
     * Some attr values (numbers and dates) that can be used in labels have to be copied and formatted for the purpose
     * of displaying in labels. This method checks if an attr is and then returns the name of the Human-Readable version of the attr.
     * @param attrName
     * @private
     */
    private getHumanReadableVersionOfLabelAttr(attrName: string) : string {
        const isDateAttr = this.attributes.findIndex(attr => attr?.name === attrName && attr?.attrType === "TIMESTAMP") > -1;
        if (isDateAttr) {
            // We will have formatted the date and added it as a separate attribute especially for labels
            attrName = "ES_DATE_" + attrName;
        }

        const isNumberAttr = this.attributes.findIndex(attr => attr?.name === attrName && (attr?.attrType === "INTEGER" || attr?.attrType === "DECIMAL")) > -1;
        if (isNumberAttr) {
            // We will have formatted the number and added it as a separate attribute especially for labels
            attrName = "ES_NUM_" + attrName;
        }

        return attrName;
    }

    private getTextExpression(labelSettings: LabelSettings): Expression | undefined {
        if(!labelSettings || !labelSettings.enabled) {
            return undefined;
        }

        const textExpr: Expression = ["format"];
        for(let i = 0; i < labelSettings.labelComponents.length; i++) {
            const labelComponent = labelSettings.labelComponents[i];
            const prefix = labelComponent.split("{");
            if(prefix.length !== 1 && prefix[0] !== "") {
                const prefixChar = prefix[0];
                textExpr.push(prefixChar);
            }

            let labelAttr = labelSettings.labelAttrs[i];
            labelAttr = this.getHumanReadableVersionOfLabelAttr(labelAttr);

            textExpr.push(["get", labelAttr]);

            const sufix = labelComponent.split("}");
            if(sufix.length !== 1 && sufix[sufix.length - 1] !== "") {
                const sufixChar = sufix[sufix.length - 1];
                textExpr.push(sufixChar);
            }

            // But the label separator in between the label components
            if(i !== labelSettings.labelComponents.length -1) {
                let separator = labelSettings?.labelSeparator;
                if(!separator || separator == ""){
                    separator = "";
                } else if(separator == "Space"){
                    separator = " ";
                } else if(separator == "New Line" || separator == "ES_NEWLINE"){
                    separator = "\n";
                } else if(separator.startsWith("{{ES_DELIM=")){ // not sure if this can happen but better to handle it
                    separator = separator.substring("{{ES_DELIM=".length, separator.length - 2)
                }

                textExpr.push(separator);
            }
        }

        const textExprWithClustersExcluded = [
            "case",
            ["==", ["get", "cluster"], true],
            "",
            textExpr
        ] as Expression;
        return textExprWithClustersExcluded;
    }

    public async generateLabelForShapeProps(layer: IMapLayer, shapeProperties: any): Promise<string | undefined> {
        //es.log.log("generateLabelForShapeProps", shapeProperties);
        if (!this.labelSettings) {
            return "";
        } else if(shapeProperties["cluster"] === true){
            //<!-- TOR9-20214 -->
            // return;

            //const pointCount = shapeProperties.point_count;
            const clusterId = parseInt(shapeProperties.cluster_id);
            /*
                clustered shape props look something like:
                {
                    "SALES": 6690,
                    "FEAT_IDS": ",573,1305,",
                    "cluster": true,
                    "cluster_id": 21023,
                    "point_count": 2,
                    "point_count_abbreviated": 2
                }
            */

            let clusterLabel = "";
            return layer.getClusterLeavesFeats(clusterId).then((pointFeats) => {
                const maxResults = parseInt(es.util.localStorage.get("es.maxMultiLabelLength") ?? 5);
                let resultCount = 0;
                for (const point of pointFeats) {
                    if (resultCount >= maxResults) {
                        clusterLabel += "<div style='text-align: center;'><span style='font-style: italic'>... " + (pointFeats.length - resultCount) + " more...</span></div>";
                        break;
                    }
                    // &#8226; circle bullet, &#8227; triangle bullet
                    clusterLabel += "&#8227; " + this.getLabelForShapeProps(point.properties, true) + "<br/>";
                    resultCount++;
                }
                //return pointCount + " feats <br/>" + clusterLabel;
                //es.log.log("clusterLabel rs", clusterLabel);
                return clusterLabel;
            });
        }

        if (shapeProperties?.ES_LABEL) {
            return es.util.HTML.encode(shapeProperties.ES_LABEL, true);
        }

        // single feat
        return this.getLabelForShapeProps(shapeProperties);
    }

    /**
     *  Generate a single label for a single feat which may have multi components/rows. Result may be used on its own or as part of a larger multi-feat label.
     * @param shapeProperties
     * @param isLeftAligned if true then adding a padding after newlines (default = false);
     * @private
     */
    private getLabelForShapeProps(shapeProperties: any, isLeftAligned = false) {
        let label: string = "";
        //es.log.log(shapeProperties);
        for (let i = 0; i < this.labelSettings.labelComponents.length; i++) {
            const labelComponent = this.labelSettings.labelComponents[i];
            const regex = /{{(.*?)}}/g;
            let match;
            let lastIndex = 0;

            while ((match = regex.exec(labelComponent)) !== null) {
                // Append the text before the placeholder
                label += es.util.HTML.encode(labelComponent.slice(lastIndex, match.index));

                let labelAttr = match[1].trim();

                labelAttr = this.getHumanReadableVersionOfLabelAttr(labelAttr);

                const attrValue = shapeProperties[labelAttr];
                if (attrValue !== undefined) {
                    label += es.util.HTML.encode(attrValue);
                }

                lastIndex = regex.lastIndex;
            }

            label += es.util.HTML.encode(labelComponent.slice(lastIndex));

            // Add the label separator in between the label components
            if (i !== this.labelSettings.labelComponents.length - 1) {
                let separator = this.labelSettings?.labelSeparator;
                if (!separator || separator == "") {
                    separator = "";
                } else if (separator == "Space") {
                    separator = " ";
                } else if (separator == "New Line" || separator == "ES_NEWLINE") {
                    separator = "<br/>" + (isLeftAligned ? "&nbsp;&nbsp;&nbsp;&nbsp;" : "");
                } else if (separator.startsWith("{{ES_DELIM=")) { // not sure if this can happen but better to handle it
                    separator = es.util.HTML.encode(separator.substring("{{ES_DELIM=".length, separator.length - 2));
                } else {
                    separator = es.util.HTML.encode(separator);
                }

                label += separator;
            }
        }

        return label;
    }

    private getOpacity(): number {
        if (this.styleSettings.defaultStyle.$type === "PolygonStyle") {
            // Labels and boxes are always fully opaque on Polygons
            return  1;
        } else {
            const mainColor = es.util.style.types[this.styleSettings.defaultStyle.$type].mainColor(this.styleSettings.defaultStyle);
            const mainTransparency = es.util.color.getTransparency(mainColor);
            return 1 - (mainTransparency / 100);
        }
    }
}
